import { useEffect, useMemo, useRef, useState } from 'react';
import { seeded } from '@lc/contracts';

const LINEART = '/art/splash_lineart.webp';
const COLOUR = '/art/splash_colour.webp';
/** The art's own shape (width / height), to place points of it on any screen it covers. */
const ART_RATIO = 1024 / 1536;
/**
 * The app's opening (apps/mobile SplashIntro): three crayon scribbles colour the inked couple in,
 * one after another and overlapping (fractions of the art, and of the run).
 */
const PASSES = [
  { from: { x: 0.36, y: 0.3 }, to: { x: 0.86, y: 1.0 }, swing: 0.62, step: 15, thick: 46, start: 0, end: 0.58 },
  { from: { x: 0.66, y: 0.34 }, to: { x: 0.18, y: 0.98 }, swing: 0.5, step: 22, thick: 30, start: 0.2, end: 0.66 },
  { from: { x: 0.3, y: 0.46 }, to: { x: 0.72, y: 0.8 }, swing: 0.42, step: 26, thick: 24, start: 0.42, end: 0.74 },
] as const;
const PAINT_MS = 2800;
const HOLD_MS = 550;
const FADE_MS = 600;
/** From here on the whole colour blooms in over the ink. */
const SETTLE_FROM = 0.72;
/** A slow push-in over the whole run, like a camera leaning in. */
const PUSH = 0.06;
/**
 * A screen this much wider than tall shows the whole picture at full height, over a blurred copy of
 * itself (cropping it to the width would leave only their heads). Matches index.html and the API's
 * handover page (min-aspect-ratio 9/10).
 */
const WIDE = 0.9;

interface Scribble {
  d: string;
  start: number;
  end: number;
  thick: number;
}

/**
 * 落筆成戀's opening beat, as in the app: the couple in ink, coloured in with wild crayon scribbles
 * while the view leans in, then the full colour blooms with a flash of light and the wordmark. It
 * holds on the finished picture until `ready` (the chapter is in), then fades into the reader.
 * With reduced motion it shows the colour picture and fades.
 */
export function Splash({ ready, waiting, onLeave, onDone }: { ready: boolean; waiting: string; onLeave: () => void; onDone: () => void }) {
  const [size, setSize] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  const [loaded, setLoaded] = useState(false);
  const [painted, setPainted] = useState(false);
  // The wordmark comes up with the colour's bloom.
  const [blooming, setBlooming] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const stage = useRef<HTMLDivElement>(null);
  const paths = useRef<Array<SVGPathElement | null>>([]);
  const colour = useRef<HTMLImageElement>(null);
  const colourBg = useRef<HTMLImageElement>(null);
  const bloom = useRef<HTMLDivElement>(null);
  const done = useRef(onDone);
  done.current = onDone;
  const leave = useRef(onLeave);
  leave.current = onLeave;
  const still = useMemo(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false, []);
  const wide = size.w / size.h >= WIDE;
  const scribbles = useMemo(() => plan(size.w, size.h, wide), [size, wide]);

  useEffect(() => {
    const on = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  // Both pictures first, so the scribbles never colour in a blank page.
  useEffect(() => {
    let left = 2;
    const one = () => {
      if (--left) return;
      setLoaded(true);
      // The page's own splash (index.html) showed the same ink picture until now; this one takes over.
      document.getElementById('boot-splash')?.remove();
    };
    for (const src of [LINEART, COLOUR]) Object.assign(new Image(), { onload: one, onerror: one, src });
  }, []);

  // The run: every frame sets how far each scribble has got, the colour's bloom and the push-in.
  useEffect(() => {
    if (!loaded) return;
    if (still) {
      for (const el of [colour.current, colourBg.current]) if (el) el.style.opacity = '1';
      setBlooming(true);
      setPainted(true);
      return;
    }
    let raf = 0;
    const t0 = performance.now() + 120;
    const frame = (now: number) => {
      const p = Math.min(1, Math.max(0, (now - t0) / PAINT_MS));
      scribbles.forEach((s, i) => {
        const el = paths.current[i];
        if (el) el.style.strokeDashoffset = String(1 - Math.min(1, Math.max(0, (p - s.start) / (s.end - s.start))));
      });
      const k = (p - SETTLE_FROM) / (1 - SETTLE_FROM);
      if (k > 0) setBlooming(true);
      for (const el of [colour.current, colourBg.current]) if (el) el.style.opacity = String(Math.min(1, Math.max(0, k)));
      if (bloom.current) bloom.current.style.opacity = String(k <= 0 || k >= 1 ? 0 : 0.22 * Math.sin(Math.PI * k));
      if (stage.current) stage.current.style.transform = `scale(${1 + PUSH * p})`;
      if (p < 1) raf = requestAnimationFrame(frame);
      else setTimeout(() => setPainted(true), HOLD_MS);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [loaded, still, scribbles]);

  // Finished and the chapter is in: fade into the reader.
  useEffect(() => {
    if (!painted || !ready) return;
    setLeaving(true);
    leave.current();
    const id = setTimeout(() => done.current(), still ? 200 : FADE_MS);
    return () => clearTimeout(id);
  }, [painted, ready, still]);

  const { w, h } = size;
  // On a wide screen the wordmark sits in the margin left of the picture, clear of the couple.
  const margin = (w - h * ART_RATIO) / 2;
  const brandAt = wide ? { left: 0, right: 'auto', width: margin, bottom: 'auto', top: 'calc(50% - 50px)' } : undefined;
  return (
    <div className={`sp${leaving ? ' sp-out' : ''}`} style={{ transitionDuration: `${still ? 200 : FADE_MS}ms` }} role="status" aria-label={waiting}>
      <div ref={stage} className="sp-stage">
        {loaded ? (
          <>
            {wide ? (
              <>
                <img className="sp-art sp-bg" src={LINEART} alt="" />
                <img ref={colourBg} className="sp-art sp-bg sp-colour" src={COLOUR} alt="" />
              </>
            ) : null}
            <img className={`sp-art${wide ? ' sp-contain' : ''}`} src={LINEART} alt="" />
            <svg className="sp-art" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden>
              <defs>
                <mask id="sp-scribble" maskUnits="userSpaceOnUse" x="0" y="0" width={w} height={h}>
                  {scribbles.map((s, i) => (
                    <path
                      key={i}
                      ref={(el) => void (paths.current[i] = el)}
                      d={s.d}
                      pathLength={1}
                      fill="none"
                      stroke="white"
                      strokeWidth={s.thick}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeDasharray="1 1"
                      strokeDashoffset={still ? 0 : 1}
                    />
                  ))}
                </mask>
              </defs>
              <image href={COLOUR} width={w} height={h} preserveAspectRatio={wide ? 'xMidYMid meet' : 'xMidYMid slice'} mask="url(#sp-scribble)" />
            </svg>
            <img ref={colour} className={`sp-art sp-colour${wide ? ' sp-contain' : ''}`} src={COLOUR} alt="" />
          </>
        ) : null}
      </div>
      <div ref={bloom} className="sp-bloom" />
      {/* Until the pictures are in: the wordmark on paper, never a blank page. */}
      {!loaded ? (
        <div className="sp-brand sp-wait">
          <img src="/art/wordmark_cn.webp" alt="落筆成戀" />
          <p>{waiting}</p>
        </div>
      ) : null}
      {blooming ? (
        <div className="sp-brand" style={brandAt}>
          <img src="/art/wordmark_cn.webp" alt="落筆成戀" />
          {painted && !ready ? <p>{waiting}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The scribbles for a screen, the same every time (the app's plan): each pass zigzags either side
 * of its line, every swing reaching a different distance and landing a little ahead or behind.
 */
function plan(width: number, height: number, wide: boolean): Scribble[] {
  const rnd = seeded('splash').next;
  // The art covers the screen (or, wide, stands at full height in the middle): where a point of it lands.
  const artW = wide ? height * ART_RATIO : Math.max(width, height * ART_RATIO);
  const artH = artW / ART_RATIO;
  const at = (p: { x: number; y: number }) => ({ x: p.x * artW - (artW - width) / 2, y: p.y * artH - (artH - height) / 2 });
  // On a wide screen the swings reach across the art, not the whole window.
  const reachW = Math.min(width, artW);
  return PASSES.map((pass) => {
    const a = at(pass.from);
    const b = at(pass.to);
    const axis = Math.hypot(b.x - a.x, b.y - a.y);
    const ux = (b.x - a.x) / axis;
    const uy = (b.y - a.y) / axis;
    const n = Math.ceil(axis / pass.step);
    const points = Array.from({ length: n + 1 }, (_, k) => {
      const t = k / n;
      const side = k % 2 ? 1 : -1;
      const wild = rnd() < 0.15 ? 1.35 : 0.65 + 0.5 * rnd();
      const reach = reachW * pass.swing * (0.5 + 0.5 * Math.sin(Math.PI * t)) * wild;
      const ahead = (rnd() - 0.5) * pass.step * 0.6;
      return { x: a.x + ux * (t * axis + ahead) - uy * side * reach, y: a.y + uy * (t * axis + ahead) + ux * side * reach };
    });
    const d = points.map((p, k) => `${k ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join('');
    return { d, start: pass.start, end: pass.end, thick: pass.thick };
  });
}
